import React, { useCallback, useMemo, useState } from "react";
import {
  PlusIcon,
  TrashIcon,
  CalendarDaysIcon,
} from "@heroicons/react/24/outline";
import type {
  MeetingRegulatoryCategory,
  MeetingRegulatoryTemplateDate,
} from "@akfdev-com/shared/src/models/Meeting";
import { meetingsApi } from "../../services/api";

// ─── UK defaults generator ──────────────────────────────────────────────────

const UK_REGULATORY_CATEGORIES: {
  value: MeetingRegulatoryCategory;
  label: string;
}[] = [
  { value: "agm", label: "AGM – Annual General Meeting" },
  { value: "service-charge", label: "Service Charge Review" },
  { value: "accounts", label: "Accounts / Budget Approval" },
  { value: "custom", label: "Custom / Other" },
];

/** UK leasehold/RMC default meetings for a given year.
 *  AGM: March (Section 20ZA LA 1985 timing);
 *  Budget approval: October (before the typical Jan-Dec service charge year);
 *  Mid-year update: June;
 *  Year-end reconciliation: January (prior year close). */
function generateUkDefaults(year: number): MeetingRegulatoryTemplateDate[] {
  return [
    {
      code: `agm-${year}`,
      title: `AGM ${year}`,
      date: `${year}-03-15`,
      category: "agm",
      removable: true,
    },
    {
      code: `budget-${year}`,
      title: `Budget Approval ${year + 1}`,
      date: `${year}-10-01`,
      category: "accounts",
      removable: true,
    },
    {
      code: `midyear-${year}`,
      title: `Mid-Year Service Charge Review ${year}`,
      date: `${year}-06-15`,
      category: "service-charge",
      removable: true,
    },
    {
      code: `reconciliation-${year}`,
      title: `Year-End Reconciliation ${year - 1}/${year}`,
      date: `${year}-01-20`,
      category: "accounts",
      removable: true,
    },
  ];
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface Props {
  buildingId: string;
  onMeetingsCreated?: () => void;
}

// ─── Component ───────────────────────────────────────────────────────────────

const RegulatoryScheduleForm: React.FC<Props> = ({
  buildingId,
  onMeetingsCreated,
}) => {
  const currentYear = useMemo(() => new Date().getFullYear(), []);

  const [year, setYear] = useState<number>(currentYear);
  const [entries, setEntries] = useState<MeetingRegulatoryTemplateDate[]>(() =>
    generateUkDefaults(currentYear),
  );
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [customTitle, setCustomTitle] = useState("");
  const [customDate, setCustomDate] = useState("");
  const [customCategory, setCustomCategory] =
    useState<MeetingRegulatoryCategory>("custom");

  // Regenerate UK defaults when the year changes, preserving manually-added custom entries
  const handleYearChange = useCallback((newYear: number) => {
    setYear(newYear);
    setEntries((prev) => {
      const customs = prev.filter(
        (e) => e.source === "manual" || e.code.startsWith("manual-"),
      );
      return [...generateUkDefaults(newYear), ...customs];
    });
    setError(null);
    setSuccess(null);
  }, []);

  const handleRemoveEntry = useCallback((code: string) => {
    setEntries((prev) => prev.filter((e) => e.code !== code));
  }, []);

  const handleAddCustom = useCallback(() => {
    if (!customTitle.trim() || !customDate) {
      setError("Title and date are required for a custom entry.");
      return;
    }
    const code = `manual-${Date.now()}`;
    const entry: MeetingRegulatoryTemplateDate = {
      code,
      title: customTitle.trim(),
      date: customDate,
      category: customCategory,
      removable: true,
    };
    setEntries((prev) =>
      [...prev, entry].sort((a, b) => a.date.localeCompare(b.date)),
    );
    setCustomTitle("");
    setCustomDate("");
    setCustomCategory("custom");
    setError(null);
  }, [customTitle, customDate, customCategory]);

  const handleGenerate = useCallback(async () => {
    if (entries.length === 0) {
      setError("Add at least one schedule entry before generating meetings.");
      return;
    }

    setGenerating(true);
    setError(null);
    setSuccess(null);

    let created = 0;
    for (const entry of entries) {
      try {
        await meetingsApi.create({
          buildingId,
          title: entry.title,
          date: entry.date,
          chairpersonContactId: "tbd",
          attendeeContactIds: ["tbd"],
          regulatorySchedule: {
            isRegulatory: true,
            category: entry.category,
            source: entry.code.startsWith("manual-") ? "manual" : "template",
            templateCode: entry.code,
          },
        });
        created += 1;
      } catch {
        // Non-fatal: continue creating remaining entries
      }
    }

    setGenerating(false);
    if (created > 0) {
      setSuccess(
        `${created} meeting${
          created !== 1 ? "s" : ""
        } created from schedule. Update chairperson and attendees on each record.`,
      );
      onMeetingsCreated?.();
    } else {
      setError(
        "No meetings were created. Please check your connection and try again.",
      );
    }
  }, [entries, buildingId, onMeetingsCreated]);

  const categoryLabel = useCallback((cat: MeetingRegulatoryCategory) => {
    return UK_REGULATORY_CATEGORIES.find((c) => c.value === cat)?.label ?? cat;
  }, []);

  return (
    <div className="bg-white shadow sm:rounded-lg p-5 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
            <CalendarDaysIcon className="h-5 w-5 text-primary-600" />
            Regulatory Meeting Schedule
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            UK leasehold / RMC defaults auto-populated. Add or remove dates,
            then generate meetings.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Year:</label>
          <input
            type="number"
            min={2020}
            max={2100}
            value={year}
            onChange={(e) => handleYearChange(Number(e.target.value))}
            className="w-24 rounded-md border-gray-300 shadow-sm text-sm"
          />
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-700">
          {success}
        </div>
      )}

      {/* Schedule entries list */}
      <div className="overflow-hidden border border-gray-200 rounded-md">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Date
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Title
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Category
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                Source
              </th>
              <th className="relative px-4 py-2">
                <span className="sr-only">Remove</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {entries.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-4 text-sm text-gray-400 text-center italic"
                >
                  No schedule entries. Add dates below or reset to UK defaults.
                </td>
              </tr>
            )}
            {entries
              .sort((a, b) => a.date.localeCompare(b.date))
              .map((entry) => (
                <tr key={entry.code} className="hover:bg-gray-50">
                  <td className="px-4 py-2 text-sm text-gray-700 whitespace-nowrap">
                    {new Date(entry.date + "T12:00:00").toLocaleDateString(
                      "en-GB",
                      {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      },
                    )}
                  </td>
                  <td className="px-4 py-2 text-sm font-medium text-gray-900">
                    {entry.title}
                  </td>
                  <td className="px-4 py-2 text-sm text-gray-600">
                    {categoryLabel(entry.category)}
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-400 capitalize">
                    {entry.code.startsWith("manual-")
                      ? "manual"
                      : "uk template"}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {entry.removable && (
                      <button
                        type="button"
                        onClick={() => handleRemoveEntry(entry.code)}
                        className="text-red-500 hover:text-red-700"
                        aria-label={`Remove ${entry.title}`}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Reset defaults shortcut */}
      <button
        type="button"
        onClick={() => handleYearChange(year)}
        className="text-xs text-primary-600 hover:underline"
      >
        Reset to UK defaults for {year}
      </button>

      {/* Add custom entry */}
      <div className="border-t pt-4 space-y-3">
        <h3 className="text-sm font-medium text-gray-700">Add custom date</h3>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
          <div className="sm:col-span-2">
            <label className="block text-xs text-gray-600 mb-1">Title</label>
            <input
              value={customTitle}
              onChange={(e) => setCustomTitle(e.target.value)}
              placeholder="e.g. Emergency Leaseholder Meeting"
              className="block w-full rounded-md border-gray-300 shadow-sm text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Date</label>
            <input
              type="date"
              value={customDate}
              onChange={(e) => setCustomDate(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm text-sm"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Category</label>
            <select
              value={customCategory}
              onChange={(e) =>
                setCustomCategory(e.target.value as MeetingRegulatoryCategory)
              }
              className="block w-full rounded-md border-gray-300 shadow-sm text-sm"
            >
              {UK_REGULATORY_CATEGORIES.map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <button
          type="button"
          onClick={handleAddCustom}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md text-gray-700 border border-gray-300 bg-white hover:bg-gray-50"
        >
          <PlusIcon className="h-4 w-4" />
          Add Date
        </button>
      </div>

      {/* Generate button */}
      <div className="border-t pt-4 flex justify-end">
        <button
          type="button"
          onClick={handleGenerate}
          disabled={generating || entries.length === 0}
          className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
        >
          {generating
            ? "Creating meetings…"
            : `Generate ${entries.length} meeting${
                entries.length !== 1 ? "s" : ""
              }`}
        </button>
      </div>
    </div>
  );
};

export default RegulatoryScheduleForm;
